import { useFormik } from 'formik';
import { useEffect, useState } from 'react';

import { ISelect, ParamEnum } from '../../../../../../shared/types/shared';
import { InitialCausality } from '../../../../../../shared/types/typifications/initialCausality';
import { formatParams } from '../../../../../../shared/utils/params';

import { asyncGetProducts } from '../../../../../../services/product.service';
import {
    asyncGetLabelParams,
    asyncGetParams,
} from '../../../../../../services/param.service';

import { initialValues, validationSchema } from './formData';
import { InitialCausalityDataProps } from './props';

export const useInitialCausalityData = ({
    data,
    onChangeData,
    onChangeFormik,
}: InitialCausalityDataProps) => {
    const [products, setproducts] = useState<ISelect[]>([]);
    const [motives, setMotives] = useState<ISelect[]>([]);
    const [submotives1, setSubmotives1] = useState<ISelect[]>([]);
    const [submotives2, setSubmotives2] = useState<ISelect[]>([]);
    const [submotives3, setSubmotives3] = useState<ISelect[]>([]);
    const [submotives4, setSubmotives4] = useState<ISelect[]>([]);
    const [typesPqr, setTypes] = useState<ISelect[]>([]);
    const [typesComplaint, setComplaint] = useState<ISelect[]>([]);

    const onSubmit = (data: InitialCausality) => {
        if (onChangeData) onChangeData(data || {});
    };

    const formik = useFormik({
        initialValues,
        validationSchema,
        onSubmit,
    });

    useEffect(() => {
        if (onChangeFormik) onChangeFormik(formik);
    }, [formik.values, formik.errors]);

    useEffect(() => {
        formik?.setFieldValue('name', data?.name || '');
        formik?.setFieldValue('product', (data?.product as ISelect)?.id || '');
        formik?.setFieldValue('typePqr', (data?.typePqr as ISelect)?.id || '');
        formik?.setFieldValue('motive', (data?.motive as ISelect)?.id || '');
        formik?.setFieldValue(
            'submotive1',
            (data?.submotive1 as ISelect)?.id || ''
        );
        formik?.setFieldValue(
            'submotive2',
            (data?.submotive2 as ISelect)?.id || ''
        );
        formik?.setFieldValue(
            'submotive3',
            (data?.submotive3 as ISelect)?.id || ''
        );
        formik?.setFieldValue(
            'submotive4',
            (data?.submotive4 as ISelect)?.id || ''
        );
        formik?.setFieldValue('category', data?.category);
        formik?.setFieldValue('process', data?.process);
        formik?.setFieldValue('subprocess', data?.subprocess);
        formik?.setFieldValue('riskId', data?.riskId);
        formik?.setFieldValue('risk', data?.risk);
        formik?.setFieldValue('consequence', data?.consequence);
        formik?.setFieldValue('riskImpact', data?.riskImpact);
        formik?.setFieldValue('sfcMotiveClaim', data?.sfcMotiveClaim);
        formik?.setFieldValue('sfcSubaccount', data?.sfcSubaccount);
        formik?.setFieldValue('sfcMotive', data?.sfcMotive);
        formik?.setFieldValue('sfcSubmotive', data?.sfcSubmotive);
        formik?.setFieldValue(
            'sfcMotiveIdComplaint',
            data?.sfcMotiveIdComplaint
        );
        formik?.setFieldValue(
            'sfcMotiveIdComplaintOld',
            data?.sfcMotiveIdComplaintOld
        );
        formik?.setFieldValue(
            'typeComplaint',
            (data?.typeComplaint as ISelect)?.id || ''
        );
        formik?.setFieldValue('motiveName', data?.motiveName);
        formik?.setFieldValue('motiveDescription', data?.motiveDescription);
        formik?.setFieldValue('active', data?.active);
    }, [data]);

    useEffect(() => {
        asyncGetProducts({ applyPqr: true }).then((items) =>
            setproducts(formatParams(items))
        );
    }, []);

    useEffect(() => {
        asyncGetLabelParams(ParamEnum.PQR_TYPE).then((items) =>
            setTypes(items)
        );
    }, []);

    useEffect(() => {
        asyncGetLabelParams(ParamEnum.TYPE_COMPLAINT).then((items) =>
            setComplaint(items)
        );
    }, []);

    useEffect(() => {
        formik.setFieldValue('name', undefined);
    }, []);

    const getNameFromId = (
        idOrIds: string | ISelect | (string | ISelect)[],
        options: ISelect[]
    ): string => {
        if (Array.isArray(idOrIds)) {
            return idOrIds
                .map((item) => {
                    if (typeof item === 'string') {
                        return (
                            options.find((opt) => opt.id === item)?.name ?? item
                        );
                    } else {
                        return item.name;
                    }
                })
                .join(', ');
        }

        if (typeof idOrIds === 'string') {
            return options.find((opt) => opt.id === idOrIds)?.name ?? idOrIds;
        }

        return idOrIds.name;
    };

    useEffect(() => {
        const productName = getNameFromId(formik.values.product, products);
        const motiveName = getNameFromId(formik.values.motive, motives);
        const submotive1Name = getNameFromId(
            formik.values.submotive1,
            submotives1
        );
        const submotive2Name = getNameFromId(
            formik.values.submotive2,
            submotives2
        );
        const submotive3Name = getNameFromId(
            formik.values.submotive3,
            submotives3
        );
        const submotive4Name = getNameFromId(
            formik.values.submotive4,
            submotives4
        );

        const nameParts = [
            productName,
            motiveName,
            submotive1Name,
            submotive2Name,
            submotive3Name,
            submotive4Name,
        ].filter((part) => part);

        const name = nameParts.join(' | ');

        if (name) formik.setFieldValue('name', name);
    }, [
        products,
        motives,
        formik.values.product,
        formik.values.motive,
        formik.values.submotive1,
        formik.values.submotive2,
        formik.values.submotive3,
        formik.values.submotive4,
    ]);

    useEffect(() => {
        if (Boolean(formik.values.typePqr))
            asyncGetParams(
                ParamEnum.PQR_MOTIVE,
                undefined,
                formik.values.typePqr
            ).then((items) => setMotives(items));

        if (!data || !Boolean(data.motive)) {
            formik.setFieldValue('motive', '');
            formik.setFieldValue('submotive1', '');
            formik.setFieldValue('submotive2', '');
            formik.setFieldValue('submotive3', '');
            formik.setFieldValue('submotive4', '');
        }
        setSubmotives1([]);
        setSubmotives2([]);
        setSubmotives3([]);
        setSubmotives4([]);
    }, [formik.values.typePqr]);

    useEffect(() => {
        if (Boolean(formik.values.motive))
            asyncGetParams(
                ParamEnum.PQR_SUBMOTIVE1,
                undefined,
                formik.values.motive
            ).then((items) => setSubmotives1(items));

        if (!data || !Boolean(data.submotive1)) {
            formik.setFieldValue('submotive1', '');
            formik.setFieldValue('submotive2', '');
            formik.setFieldValue('submotive3', '');
            formik.setFieldValue('submotive4', '');
        }
        setSubmotives2([]);
        setSubmotives3([]);
        setSubmotives4([]);
    }, [formik.values.motive]);

    useEffect(() => {
        if (Boolean(formik.values.submotive1))
            asyncGetParams(
                ParamEnum.PQR_SUBMOTIVE2,
                undefined,
                formik.values.submotive1
            ).then((items) => setSubmotives2(items));

        if (!data || !Boolean(data.submotive2)) {
            formik.setFieldValue('submotive2', '');
            formik.setFieldValue('submotive3', '');
            formik.setFieldValue('submotive4', '');
        }
        setSubmotives3([]);
        setSubmotives4([]);
    }, [formik.values.submotive1]);

    useEffect(() => {
        if (Boolean(formik.values.submotive2))
            asyncGetParams(
                ParamEnum.PQR_SUBMOTIVE3,
                undefined,
                formik.values.submotive2
            ).then((items) => setSubmotives3(items));
        if (!data || !Boolean(data.submotive3)) {
            formik.setFieldValue('submotive3', '');
            formik.setFieldValue('submotive4', '');
        }
        setSubmotives4([]);
    }, [formik.values.submotive2]);

    useEffect(() => {
        if (Boolean(formik.values.submotive3))
            asyncGetParams(
                ParamEnum.PQR_SUBMOTIVE4,
                undefined,
                formik.values.submotive3
            ).then((items) => setSubmotives4(items));
        if (!data || !Boolean(data.submotive4)) {
            formik.setFieldValue('submotive4', '');
        }
    }, [formik.values.submotive3]);

    return {
        formik,
        products,
        motives,
        submotives1,
        submotives2,
        submotives3,
        submotives4,
        typesPqr,
        typesComplaint,
    };
};
